import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Tooltip,
    Box,
    AppBar,
    Toolbar
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {useNavigate, useLocation} from "react-router-dom";
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';

import { IfPermission } from "./permission/IfPermission";
import {
    ADMIN_CONTESTED_CASE_FORMS_EDIT,
    ADMIN_FIRM_EDIT,
    ADMIN_PERMISSION_EDIT,
    ADMIN_PERMISSION_VIEW, CUSTOM_EXPORTS_ACCESS,
    LS_DOCUMENTS_VIEW, SEMANTIC_LAYER_ACCESS
} from "./permission/permission-consts";
import lsLogo from "../assets/LegalStreamLogo.avif";
import ProfileMenu from "./profile/ProfileMenu";
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined'
import {useDispatch, useSelector} from "react-redux";
import {appUiAction} from "../store/slices/appUi";
import {FullscreenExit} from "@mui/icons-material";


const drawerWidth = 240;
const collapsedWidth = 60;

const Header = ({onSignOut, open, onToggle}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {zenView} = useSelector(state => state.appUi)
    const dispatch = useDispatch();

    const handleToggle = () => onToggle();

    const handleNavigation = (route) => {
        navigate(route);
    };

    // Check if any Access Control child is selected
    const accessControlSelected = ["/access-control/users", "/access-control/roles", "/access-control/permissions"].includes(location.pathname);

    const getMainItemSx = (isOpen) => (
        isOpen ? {
            px: 2,
            py: 1.5,
            color: "#fff",
            "&.Mui-selected": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                borderLeft: "4px solid #fff",
                "&:hover": {backgroundColor: "rgba(255, 255, 255, 0.2)"}
            },
            "&:hover": {backgroundColor: "rgba(255, 255, 255, 0.08)"}
        } : {
            px: 1.5,
            py: 1.5,
            justifyContent: "center",
            color: "#fff",
            "&.Mui-selected": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                borderLeft: "4px solid #fff",
                "&:hover": {backgroundColor: "rgba(255, 255, 255, 0.2)"}
            },
            "&:hover": {backgroundColor: "rgba(255, 255, 255, 0.08)"}
        }
    );

    const getNestedItemSx = (isOpen) => (
        isOpen ? {
            pl: 7,
            pr: 2,
            py: 1,
            color: "#fff",
            "&.Mui-selected": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                borderLeft: "4px solid #4fc3f7",
                "&:hover": {backgroundColor: "rgba(255, 255, 255, 0.2)"}
            },
            "&:hover": {backgroundColor: "rgba(255, 255, 255, 0.08)"}
        } : {
            display: "none"
        }
    );


    return (
        <>
            {/* Top AppBar */}
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: "primary.main",
                    boxShadow: "none",
                    ...(zenView ? {display: 'none'} : {})
                }}
            >
                <Toolbar sx={{display: "flex", justifyContent: "flex-end"}}>
                    {window.location.pathname.startsWith('/powerbi') && (
                        <Tooltip title="Enter Zen View" arrow placement="bottom">
                            <IconButton
                                onClick={() => dispatch(appUiAction.setZenView(true))}
                                sx={{
                                    color: 'white',
                                    mr: 2,
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                                size="small"
                            >
                                <FullscreenExit sx={{transform: 'rotate(90deg)', fontSize: '20px'}}/>
                            </IconButton>
                        </Tooltip>
                    )}

                    <ProfileMenu onSignOut={onSignOut}/>
                </Toolbar>
            </AppBar>

            {/* Sidebar Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    ...(zenView ? {display: 'none'} : {}),
                    width: open ? drawerWidth : collapsedWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: open ? drawerWidth : collapsedWidth,
                        transition: "width 0.3s",
                        overflowX: "hidden",
                        boxSizing: "border-box",
                        backgroundColor: "primary.main",
                        color: "#fff",
                        position: "fixed",
                        height: "100vh",
                        top: 0,
                        paddingTop: 0,
                        zIndex: 1201
                    },
                }}
            >

                {/* Header: Logo when expanded, Breadcrumb + Toggle when collapsed */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1,
                        minHeight: open ? 64 : 40,
                    }}
                >
                    {open ? (
                        <>
                            {/* Logo */}
                            <img
                                onClick={() => handleNavigation("/home")}
                                src={lsLogo}
                                alt="Company Logo"
                                style={{width: "120px", paddingLeft: '10px', height: "auto", cursor: "pointer"}}
                            />

                            {/* Collapse Icon */}
                            <Tooltip title="Collapse menu" placement="right">
                                <IconButton onClick={handleToggle} sx={{color: "#fff"}}>
                                    <ChevronLeftIcon/>
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        // Collapsed state: only expand icon
                        <Tooltip title="Expand menu" placement="right">
                            <IconButton onClick={handleToggle} sx={{color: "#fff"}}>
                                <MenuIcon/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <List sx={{pt: 0}}>

                    {/* ======================= */}
                    {/* Dashboards */}
                    {/* ======================= */}
                    <Tooltip title="Dashboards" placement="right" disableHoverListener={open}>
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={location.pathname === "/dashboards" || location.pathname === "/home"}
                                onClick={() => handleNavigation("/dashboards")}
                                sx={getMainItemSx(open)}
                            >
                                <ListItemIcon sx={{color: "#fff", minWidth: open ? 36 : "auto"}}>
                                    <DashboardOutlinedIcon/>
                                </ListItemIcon>
                                {open && <ListItemText primary="Dashboards"/>}
                            </ListItemButton>
                        </ListItem>
                    </Tooltip>

                    {/* ======================= */}
                    {/* Documents Section */}
                    {/* ======================= */}
                    <IfPermission permission={LS_DOCUMENTS_VIEW}>
                        <Tooltip title="Documents" placement="right" disableHoverListener={open}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={location.pathname === "/documents" || location.pathname === "/naming-issue-files" || location.pathname === "/doctype-alias-mapping" || location.pathname === "/archived-documents"}
                                    onClick={() => handleNavigation("/documents")}
                                    sx={getMainItemSx(open)}
                                >
                                    <ListItemIcon sx={{color: "#fff", minWidth: open ? 36 : "auto"}}>
                                        <DocumentScannerOutlinedIcon/>
                                    </ListItemIcon>
                                    {open && <ListItemText primary="Documents"/>}
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    </IfPermission>

                    {/* ======================= */}
                    {/* Firms Section */}
                    {/* ======================= */}
                    <IfPermission permission={ADMIN_FIRM_EDIT}>
                        <Tooltip title="Firms" placement="right" disableHoverListener={open}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={location.pathname === "/firms"}
                                    onClick={() => handleNavigation("/firms")}
                                    sx={getMainItemSx(open)}
                                >
                                    <ListItemIcon sx={{color: "#fff", minWidth: open ? 36 : "auto"}}>
                                        <BalanceOutlinedIcon/>
                                    </ListItemIcon>
                                    {open && <ListItemText primary="Firms"/>}
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    </IfPermission>

                    <IfPermission anyPermissions={[SEMANTIC_LAYER_ACCESS]}>
                        <Tooltip title="Semantic Layer" placement="right" disableHoverListener={open}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={location.pathname.startsWith("/semantic-layer")}
                                    onClick={() => handleNavigation("/semantic-layer")}
                                    sx={getMainItemSx(open)}
                                >
                                    <ListItemIcon sx={{color: "#fff", minWidth: open ? 36 : "auto"}}>
                                        <HubOutlinedIcon/>
                                    </ListItemIcon>
                                    {open && <ListItemText primary="Semantic Layer"/>}
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    </IfPermission>

                    <IfPermission anyPermissions={[CUSTOM_EXPORTS_ACCESS]}>
                        <Tooltip title="Datasets" placement="right" disableHoverListener={open}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={location.pathname.startsWith("/custom-reports")}
                                    onClick={() => handleNavigation("/custom-reports")}
                                    sx={getMainItemSx(open)}
                                >
                                    <ListItemIcon sx={{color: "#fff", minWidth: open ? 36 : "auto"}}>
                                        <DatasetOutlinedIcon/>
                                    </ListItemIcon>
                                    {open && <ListItemText primary="Custom Reports"/>}
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    </IfPermission>

                    <IfPermission anyPermissions={[ADMIN_CONTESTED_CASE_FORMS_EDIT]}>
                        <Tooltip title="Contested Case Forms" placement="right" disableHoverListener={open}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={location.pathname.startsWith("/contested-case-forms")}
                                    onClick={() => handleNavigation("/contested-case-forms")}
                                    sx={getMainItemSx(open)}
                                >
                                    <ListItemIcon sx={{color: "#fff", minWidth: open ? 36 : "auto"}}>
                                        <PostAddOutlinedIcon />
                                    </ListItemIcon>
                                    {open && <ListItemText primary="Contested Case Forms"/>}
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    </IfPermission>

                    {/* ======================= */}
                    {/* Access Control Section */}
                    {/* ======================= */}
                    <IfPermission anyPermissions={[ADMIN_PERMISSION_VIEW, ADMIN_PERMISSION_EDIT]}>
                        {/* Parent "Access Control" */}
                        <Tooltip title="Access Control" placement="right" disableHoverListener={open}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={accessControlSelected}
                                    onClick={() => handleNavigation("/access-control/users")}
                                    sx={getMainItemSx(open)}
                                >
                                    <ListItemIcon sx={{color: "#fff", minWidth: open ? 36 : "auto"}}>
                                        <AdminPanelSettingsIcon/>
                                    </ListItemIcon>
                                    {open && <ListItemText primary="Access Control"/>}
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>

                        {/* Users */}
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={location.pathname === "/access-control/users"}
                                onClick={() => handleNavigation("/access-control/users")}
                                sx={getNestedItemSx(open)}
                            >
                                {open && <ListItemText primary="Users"/>}
                            </ListItemButton>
                        </ListItem>

                        {/* Roles */}
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={location.pathname === "/access-control/roles"}
                                onClick={() => handleNavigation("/access-control/roles")}
                                sx={getNestedItemSx(open)}
                            >
                                {open && <ListItemText primary="Roles"/>}
                            </ListItemButton>
                        </ListItem>

                        {/* Permissions */}
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={location.pathname === "/access-control/permissions"}
                                onClick={() => handleNavigation("/access-control/permissions")}
                                sx={getNestedItemSx(open)}
                            >
                                {open && <ListItemText primary="Permissions"/>}
                            </ListItemButton>
                        </ListItem>

                    </IfPermission>
                </List>

            </Drawer>
        </>
    );
};

export default Header;